import { useCreatePayUp, useDeletePayUp, useUploadPayUpPicture } from '@hooks'
import { useCreateExpense } from '@hooks/useCreateExpense'
import { useDeleteExpense } from '@hooks/useDeleteExpense'
import { useDeleteExpensePicture } from '@hooks/useDeleteExpensePicture'
import { useUpdateExpense } from '@hooks/useUpdateExpense'
import { useUploadExpensePicture } from '@hooks/useUploadExpensePicture'
import { GetExpenseResponse, GetPayUpType, GetUserType } from '@orbit/api-types'
import toast from 'react-hot-toast'
import { AddPayUpData } from './AddPayUpModal'

export interface AddExpenseData {
	name: string
	amount: number
	paidByUserId: number
	splitType: string
	currencyCode: string
	participants: { userId: number; splitAmount: number | null }[]
	imageFile?: File
}

export interface EditExpenseData extends AddExpenseData {
	deleteImage?: boolean
}

interface Options {
	tripId: string | undefined
	me: GetUserType | undefined
	editingExpense: GetExpenseResponse | null
	deletingExpense: GetExpenseResponse | null
	deletingPayUp: GetPayUpType | null
	onCloseAddExpense: () => void
	onCloseEditExpense: () => void
	onCloseDeleteExpense: () => void
	onCloseAddPayUp: () => void
	onCloseDeletePayUp: () => void
	onCloseViewingPayUp: () => void
}

export const useBalancesMutations = (opts: Options) => {
	const createExpense = useCreateExpense()
	const updateExpense = useUpdateExpense()
	const deleteExpense = useDeleteExpense()
	const uploadExpensePicture = useUploadExpensePicture()
	const deleteExpensePicture = useDeleteExpensePicture()

	const createPayUp = useCreatePayUp()
	const deletePayUp = useDeletePayUp()
	const uploadPayUpPicture = useUploadPayUpPicture()

	const handleAddExpense = (data: AddExpenseData) => {
		const { tripId, onCloseAddExpense } = opts
		if (!tripId) return
		createExpense.mutate(
			{
				tripId,
				expenseData: {
					name: data.name,
					amount: data.amount,
					paidByUserId: data.paidByUserId,
					splitType: data.splitType as 'Equally' | 'Value' | 'Percent',
					participants: data.participants,
					currencyCode: data.currencyCode,
				},
			},
			{
				onSuccess: created => {
					if (data.imageFile) {
						uploadExpensePicture.mutate(
							{ tripId, expenseId: created.id, file: data.imageFile },
							{
								onSuccess: () => {
									toast.success('Expense added!')
									onCloseAddExpense()
								},
								onError: () => {
									toast.success('Expense added, but image upload failed.')
									onCloseAddExpense()
								},
							},
						)
					} else {
						toast.success('Expense added!')
						onCloseAddExpense()
					}
				},
				onError: () => toast.error('Failed to add expense. Please try again.'),
			},
		)
	}

	const handleEditExpense = (data: EditExpenseData) => {
		const { tripId, editingExpense, onCloseEditExpense } = opts
		if (!tripId || !editingExpense) return

		const handleImageAfterUpdate = () => {
			const existingFileId = editingExpense.fileIds?.[0]
			if (data.deleteImage && existingFileId) {
				deleteExpensePicture.mutate(
					{ tripId, expenseId: editingExpense.id, fileId: existingFileId },
					{
						onSuccess: () => {
							toast.success('Expense updated!')
							onCloseEditExpense()
						},
						onError: () => {
							toast.success('Expense updated, but image removal failed.')
							onCloseEditExpense()
						},
					},
				)
			} else if (data.imageFile) {
				uploadExpensePicture.mutate(
					{ tripId, expenseId: editingExpense.id, file: data.imageFile },
					{
						onSuccess: () => {
							toast.success('Expense updated!')
							onCloseEditExpense()
						},
						onError: () => {
							toast.success('Expense updated, but image upload failed.')
							onCloseEditExpense()
						},
					},
				)
			} else {
				toast.success('Expense updated!')
				onCloseEditExpense()
			}
		}

		updateExpense.mutate(
			{
				tripId,
				expenseId: editingExpense.id,
				expenseData: {
					name: data.name,
					amount: data.amount,
					paidByUserId: data.paidByUserId,
					splitType: data.splitType as 'Equally' | 'Value' | 'Percent',
					participants: data.participants,
					currencyCode: data.currencyCode,
				},
			},
			{
				onSuccess: handleImageAfterUpdate,
				onError: () => toast.error('Failed to update expense. Please try again.'),
			},
		)
	}

	const handleDeleteExpense = () => {
		const { tripId, deletingExpense, onCloseDeleteExpense, onCloseEditExpense } = opts
		if (!tripId || !deletingExpense) return
		deleteExpense.mutate(
			{ tripId, expenseId: deletingExpense.id },
			{
				onSuccess: () => {
					toast.success('Expense deleted')
					onCloseDeleteExpense()
					onCloseEditExpense()
				},
				onError: () => toast.error('Failed to delete expense. Please try again.'),
			},
		)
	}

	const handleAddPayUp = (data: AddPayUpData) => {
		const { tripId, me, onCloseAddPayUp } = opts
		if (!tripId || !me) return
		createPayUp.mutate(
			{
				tripId,
				currentUserId: me.id,
				payUpData: {
					fromUserId: data.fromUserId,
					toUserId: data.toUserId,
					amount: data.amount,
					currencyCode: data.currencyCode,
				},
			},
			{
				onSuccess: created => {
					if (data.imageFile) {
						uploadPayUpPicture.mutate(
							{ tripId, payUpId: created.id, file: data.imageFile },
							{
								onSuccess: () => {
									toast.success('Payup recorded!')
									onCloseAddPayUp()
								},
								onError: () => {
									toast.success('Payup recorded, but image upload failed.')
									onCloseAddPayUp()
								},
							},
						)
					} else {
						toast.success('Payup recorded!')
						onCloseAddPayUp()
					}
				},
				onError: () => toast.error('Failed to record payup. Please try again.'),
			},
		)
	}

	const handleDeletePayUp = () => {
		const { tripId, deletingPayUp, onCloseDeletePayUp, onCloseViewingPayUp } = opts
		if (!tripId || !deletingPayUp) return
		deletePayUp.mutate(
			{ tripId, payUpId: deletingPayUp.id },
			{
				onSuccess: () => {
					toast.success('Payup deleted')
					onCloseDeletePayUp()
					onCloseViewingPayUp()
				},
				onError: () => toast.error('Failed to delete payup. Please try again.'),
			},
		)
	}

	return {
		handleAddExpense,
		handleEditExpense,
		handleDeleteExpense,
		handleAddPayUp,
		handleDeletePayUp,
		isExpenseSubmitting:
			createExpense.isPending || uploadExpensePicture.isPending || deleteExpensePicture.isPending,
		isEditExpenseSubmitting:
			updateExpense.isPending || uploadExpensePicture.isPending || deleteExpensePicture.isPending,
		isPayUpSubmitting: createPayUp.isPending || uploadPayUpPicture.isPending,
		isDeletingPayUpPending: deletePayUp.isPending,
	}
}
